import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core'
import { CommonModule, DatePipe } from '@angular/common'
import { Announcement } from '../../../models/announcement'
import { IonicModule, AlertController, ModalController, PopoverController } from '@ionic/angular'
import { AnnouncementsService } from '../../../services/domain/announcements/announcements.service'
import { AnnouncementViewerComponent } from '../announcement-viewer/announcement-viewer.component'
import { UsersService } from '../../../services/domain/users.service'
import { map } from 'rxjs/operators'
import { combineLatest, firstValueFrom, Observable } from 'rxjs'
import { AnnouncementItemPopoverMenuComponent } from '../announcement-item-popover-menu/announcement-item-popover-menu.component'
import { BypassHtmlSanitizerPipe } from '../../../shared/pipes/bypass-html-sanitizer.pipe'
import { NestedEllipsisModule } from 'ngx-nested-ellipsis'

@Component({
  selector: 'app-announcement-list',
  standalone: true,
  imports: [CommonModule, IonicModule, DatePipe, BypassHtmlSanitizerPipe, NestedEllipsisModule],
  templateUrl: './announcement-list.component.html',
  styleUrls: ['./announcement-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnnouncementListComponent implements OnInit, OnChanges {
  private alertCtrl = inject(AlertController)
  private announcementsService = inject(AnnouncementsService)
  private modalCtrl = inject(ModalController)
  private usersService = inject(UsersService)
  private popoverCtrl = inject(PopoverController)

  @Input() announcements: Announcement[]

  @Output() replyToMessage = new EventEmitter<Announcement>()

  sortedAnnouncements: Announcement[]

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    console.log('ngonchanges', changes)
    for (const propName in changes) {
      if (changes.hasOwnProperty(propName)) {
        switch (propName) {
          case 'announcements':
            if (!this.announcements) break
            this.sortedAnnouncements = this.announcements.sort((a, b) => {
              if (a.publishedAt < b.publishedAt) return 1
              if (a.publishedAt > b.publishedAt) return -1
              return 0
            })
        }
      }
    }
  }

  async openMessage(msg: Announcement, openInEditMode = false) {
    const canEdit = await firstValueFrom(this.canEdit(msg))

    const modal = await this.modalCtrl.create({
      component: AnnouncementViewerComponent,
      showBackdrop: true,
      componentProps: { message: msg, canEdit, openInEditMode },
      cssClass: ['announcementViewerModal', 'auto-height'],
    })

    modal.onDidDismiss().then((data) => {
      const command = data.data
      if (command === 'reply') this.replyToMessage.emit(msg)
    })

    await modal.present()

    this.setRead(msg)
  }
  canEdit(a: Announcement) {
    return combineLatest([this.isCurrentUserAuthor(a), this.usersService.isCurrentUserAdmin()]).pipe(
      map(([isAuthor, isAdmin]) => isAuthor || isAdmin),
    )
  }
  async editAnnouncement(a: Announcement, event?: MouseEvent) {
    event?.stopPropagation()
    await this.openMessage(a, true)
  }

  isCurrentUserAuthor(a: Announcement): Observable<boolean> {
    return this.usersService.getCurrentUser().pipe(map((user) => a.userId === user.id))
  }

  async deleteAnnouncementWithConfirmation(a: Announcement, event?: MouseEvent) {
    event?.stopPropagation()

    const alert = await this.alertCtrl.create({
      header: 'Opravdu smazat?',
      message: 'Opravdu si přejete smazat toto oznámení?',
      buttons: [
        {
          text: 'Zrušit',
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {},
        },
        {
          text: 'Smazat',
          cssClass: 'primary',
          handler: () => {
            this.deleteAnnouncement(a)
          },
        },
      ],
    })

    await alert.present()
  }

  deleteAnnouncement(a: Announcement) {
    this.announcementsService.delete(a)
  }

  setRead(a: Announcement, event?) {
    event?.stopPropagation()
    this.announcementsService.markMessageRead(a)
  }

  trackByFn(index: number, item: Announcement) {
    return item.id
  }

  async openPopover(event: MouseEvent, a: Announcement) {
    const canEdit = await firstValueFrom(this.canEdit(a))

    const popover = await this.popoverCtrl.create({
      component: AnnouncementItemPopoverMenuComponent,
      componentProps: { announcement: a, canEdit },
      reference: 'trigger',
      event,
      showBackdrop: false,
    })

    await popover.present()

    const { role } = await popover.onDidDismiss()
    switch (role) {
      case 'edit':
        return await this.editAnnouncement(a)
      case 'delete':
        return await this.deleteAnnouncementWithConfirmation(a)
      case 'archive':
        return await this.archive(a)
      case 'publish':
        return await this.publishArchived(a)
    }

    console.log(`Popover dismissed with role: ${role}`, a)
  }

  private async publishArchived(a: Announcement) {
    return await this.announcementsService.patch(a.id, {
      publishedAt: new Date(),
    })
  }

  private async archive(a: Announcement) {
    return await this.announcementsService.patch(a.id, { publishedAt: null })
  }
}
